import { useEffect, useRef, useState } from "react";
import ReportTable from "./ReportTable";
import { useAccrualActionButtons, useAccrualReport } from "./useAccrualReport";
import { AESEncryption } from "../../../CommonMethhods/CommonMethods";
import CommonSearch from "../../../CommonMethhods/CommonSearch";
import { toast } from "react-toastify";
import { Constants } from "../../../CommonMethhods/Constants";
import SmallLoader from "../../../Components/Common/smallLoader";

const PAGE_SIZE = 50;

const AccrualChargesContainer = ({ presetClientCode }) => {
  const { isLoading, data, error, fetchAccrualChargesReportData } =
    useAccrualReport();
  const { isLoading: actionBtnsLoading, downloadReportFile } =
    useAccrualActionButtons();

  const [pageNo, setPageNo] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const [clientCode, setClientCode] = useState("");
  const observerRef = useRef(null);

  const clientCodeFromDashboard = presetClientCode;

  const loadedRecords = data?.itemizedList?.length || 0;
  const totalCount = data?.totalCount || 0;

  useEffect(() => {
    setHasMore(loadedRecords < totalCount);
  }, [loadedRecords, totalCount]);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      const target = entries[0];

      if (target.isIntersecting && !isLoading && hasMore) {
        setPageNo((prev) => prev + 1);
      }
    });

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [isLoading, hasMore]);

  const handleClientCodeChange = (code) => {
    setClientCode(code);
    setPageNo(1);
  };

  useEffect(() => {
    const encrypted = AESEncryption({
      clientCode: clientCodeFromDashboard || clientCode,
      fileType: "GRID",
      PageNo: pageNo,
      PageSize: PAGE_SIZE,
    });

    fetchAccrualChargesReportData(encrypted);
  }, [
    clientCode,
    fetchAccrualChargesReportData,
    pageNo,
    clientCodeFromDashboard,
  ]);

  const handleActionBtnClick = async (action) => {
    const encrypted = AESEncryption({
      clientCode: clientCode,
      fileType: action,
      PageNo: pageNo,
      PageSize: PAGE_SIZE,
    });
    const { success, data } = await downloadReportFile(encrypted);
    if (success && data) {
      window.open(
        data.data,
        action === "pdf" ? "_blank" : "_self",
        "noopener,noreferrer",
      );
    } else {
      toast.error(data?.msg || Constants.GENERIC_ERROR_MESSAGE);
    }
  };

  return (
    <div className="accrual-report__container">
      <div
        style={{
          justifyContent: clientCodeFromDashboard
            ? "flex-end"
            : "space-between",
        }}
        className="accrual-report__filters"
      >
        {!clientCodeFromDashboard && (
          <CommonSearch
            clientCode={clientCode}
            setClientCode={handleClientCodeChange}
            label="Client Code"
          />
        )}

        <div className="accrual-report__actions">
          <button
            className="accrual-report__action-btn accrual-report__action-btn--excel"
            onClick={() => handleActionBtnClick("excel")}
          >
            {actionBtnsLoading.excel ? (
              <SmallLoader />
            ) : (
              <>
                <span className="icon-excelsvg"></span>
                Excel
              </>
            )}
          </button>

          <button
            className="accrual-report__action-btn accrual-report__action-btn--pdf"
            onClick={() => handleActionBtnClick("pdf")}
          >
            {actionBtnsLoading.pdf ? (
              <SmallLoader />
            ) : (
              <>
                <span className="icon-pdfsvg"></span>
                PDF
              </>
            )}
          </button>
        </div>
      </div>

      <ReportTable
        loading={isLoading}
        dataSource={data?.itemizedList}
        error={error}
        lastRowRef={observerRef}
      />
    </div>
  );
};

export default AccrualChargesContainer;
